import { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Info, BarChart3, DollarSign, Settings, Star, Pencil, UserPlus, TrendingUp } from 'lucide-react';
import { groupsAPI } from '../../../../../services';
import { formatNumber, formatCurrency, formatDate, getGroupId } from '../utils';

export default function GroupDetailPanel({ groupId, onClose, onEdit, onUsers, onInstruments }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const result = await groupsAPI.getGroupDetails(groupId);
        let data = result;
        if (result?.data?.data?.group) data = result.data.data.group;
        else if (result?.data?.data) data = result.data.data;
        else if (result?.data) data = result.data;
        if (!cancelled) setDetails(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load group details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  // Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const CATEGORIES = ['stocks', 'crypto', 'indices', 'commodities', 'forex'];

  return (
    <div className="gm-panel-overlay">
      <div className="gm-panel-backdrop" onClick={onClose} />
      <div className="gm-panel animate-slide-in-right">
        {/* Header */}
        <div className="gm-panel-header">
          <h2 className="gm-panel-title">
            <Info size={20} color="var(--admin-primary)" />
            Group Details
          </h2>
          <button onClick={onClose} className="gm-panel-close">
            <X size={20} />
          </button>
        </div>

        {loading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div className="gm-spinner" />
              <p className="gm-loading-text">Loading details...</p>
            </div>
          </div>
        )}

        {error && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
              <AlertCircle size={40} color="#f87171" />
              <p style={{ fontSize: '0.875rem', color: '#991b1b' }}>{error}</p>
              <button onClick={onClose} className="gm-btn-secondary">Close</button>
            </div>
          </div>
        )}

        {details && (
          <div className="gm-panel-body">
            {/* Title section */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--admin-text-primary)', margin: 0 }}>
                    {details.name}
                  </h3>
                  {details.isDefault && (
                    <span className="gm-badge-default">
                      <Star size={12} fill="#f59e0b" color="#f59e0b" /> Default
                    </span>
                  )}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-secondary)', marginTop: 4 }}>
                  {details.description || 'No description'}
                </p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="gm-detail-quick-actions">
              <button onClick={() => { onEdit?.(getGroupId(details)); onClose(); }}
                className="gm-detail-quick-btn gm-detail-quick-btn-teal">
                <Pencil size={14} /> Edit
              </button>
              <button onClick={() => { onUsers?.(getGroupId(details), details.name); onClose(); }}
                className="gm-detail-quick-btn gm-detail-quick-btn-blue">
                <UserPlus size={14} /> Users
              </button>
              <button onClick={() => { onInstruments?.(getGroupId(details), details.name); onClose(); }}
                className="gm-detail-quick-btn gm-detail-quick-btn-amber">
                <TrendingUp size={14} /> Instruments
              </button>
            </div>

            {/* Basic info card */}
            <div className="gm-detail-card">
              <h4><Info size={16} color="var(--admin-primary)" /> Basic Information</h4>
              <div className="gm-detail-grid">
                <div>
                  <p className="gm-detail-item-label">Group ID</p>
                  <p className="gm-detail-item-value gm-detail-item-value-mono" style={{ fontSize: '0.75rem' }}>
                    {getGroupId(details)}
                  </p>
                </div>
                <div>
                  <p className="gm-detail-item-label">Status</p>
                  <span className={`gm-badge ${details.isActive !== false ? 'gm-badge-active' : 'gm-badge-inactive'}`}>
                    <span className={`gm-badge-dot ${details.isActive !== false ? 'gm-badge-dot-active' : 'gm-badge-dot-inactive'}`} />
                    {details.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <p className="gm-detail-item-label">Created</p>
                  <p className="gm-detail-item-value">{formatDate(details.createdAt)}</p>
                </div>
                <div>
                  <p className="gm-detail-item-label">Users</p>
                  <p className="gm-detail-item-value">{details.userCount || 0}</p>
                </div>
              </div>
            </div>

            {/* Trading params card */}
            <div className="gm-detail-card">
              <h4><Settings size={16} color="var(--admin-primary)" /> Trading Parameters</h4>
              <div className="gm-detail-grid">
                <div>
                  <p className="gm-detail-item-label">Commission</p>
                  <p className="gm-detail-item-value gm-detail-item-value-mono">
                    {formatNumber((details.commission || 0) * 100, 4)}%
                  </p>
                </div>
                <div>
                  <p className="gm-detail-item-label">Swap Rate</p>
                  <p className="gm-detail-item-value gm-detail-item-value-mono">
                    {formatNumber((details.swap || 0) * 100, 4)}%
                  </p>
                </div>
                <div>
                  <p className="gm-detail-item-label">Swap Status</p>
                  <p className="gm-detail-item-value" style={{ color: details.swapEnabled ? '#16a34a' : 'var(--admin-text-light)' }}>
                    {details.swapEnabled ? `Active @ ${details.swapTime || '22:00'} UTC · 3× ${details.tripleSwapDay || 'Wednesday'}` : 'Disabled'}
                  </p>
                </div>
                <div>
                  <p className="gm-detail-item-label">Default Spread</p>
                  <p className="gm-detail-item-value gm-detail-item-value-mono">
                    {formatNumber((details.defaultSpread || 0) * 100, 5)}%
                  </p>
                </div>
                <div>
                  <p className="gm-detail-item-label">Max Position</p>
                  <p className="gm-detail-item-value">{formatCurrency(details.maxPositionSize || 0)}</p>
                </div>
              </div>
            </div>

            {/* Leverage settings card */}
            <div className="gm-detail-card">
              <h4><BarChart3 size={16} color="var(--admin-primary)" /> Leverage Settings</h4>
              <div>
                {CATEGORIES.map((cat) => {
                  const ls = details.leverageSettings?.[cat];
                  if (!ls) return null;
                  return (
                    <div key={cat} className="gm-detail-leverage-row">
                      <span className="gm-detail-leverage-cat">{cat}</span>
                      <div className="gm-detail-leverage-vals">
                        <span>Max: <span>{ls.maxLeverage || ls.max || 'N/A'}:1</span></span>
                        <span>Current: <span style={{ color: 'var(--admin-primary)' }}>{ls.currentLeverage || ls.default || 'N/A'}:1</span></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Instruments count */}
            <div className="gm-detail-card">
              <h4><TrendingUp size={16} color="var(--admin-primary)" /> Allowed Instruments</h4>
              <p className="gm-detail-big-number">
                {(details.allowedInstruments || []).length}
              </p>
              <p style={{ fontSize: '0.75rem', color: 'var(--admin-text-secondary)', marginTop: 2 }}>
                instruments assigned to this group
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
